import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import './GoogleClassroomStatus.css';

interface GoogleClassroomStatusData {
  connected: boolean;
  needs_refresh: boolean;
  has_tokens: boolean;
}

const GoogleClassroomStatus: React.FC = () => {
  const { token, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<GoogleClassroomStatusData>({
    connected: false,
    needs_refresh: false,
    has_tokens: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Check status on component mount and periodically
  useEffect(() => {
    if (token && isAuthenticated) {
      checkStatus();
      
      // Check status every 30 seconds
      const interval = setInterval(checkStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [token, isAuthenticated]);

  const checkStatus = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/auth/google-classroom/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error checking Google Classroom status:', error);
      setStatus({
        connected: false,
        needs_refresh: false,
        has_tokens: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    if (status.connected || isConnecting || !token || !isAuthenticated) return;
    
    try {
      setIsConnecting(true);
      
      const response = await fetch('/api/auth/google-classroom/connect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && data.authorization_url) {
        // Redirect user to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || 'Failed to initiate OAuth flow');
      }
      
    } catch (error: any) {
      console.error('Error connecting to Google Classroom:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const getStatusText = () => {
    if (isLoading) return 'Checking...';
    if (isConnecting) return 'Connecting...';
    if (status.connected) return 'Google Classroom Connected';
    if (status.has_tokens && status.needs_refresh) return 'Connection Expired';
    return 'Google Classroom Disconnected';
  };

  const getStatusClass = () => {
    if (isLoading || isConnecting) return 'loading';
    if (status.connected) return 'connected';
    if (status.has_tokens && status.needs_refresh) return 'expired';
    return 'disconnected';
  };

  const isClickable = () => {
    return !status.connected && !isLoading && !isConnecting;
  };

  return (
    <div 
      className={`google-classroom-status ${getStatusClass()} ${isClickable() ? 'clickable' : ''}`}
      onClick={isClickable() ? handleConnect : undefined}
      role={isClickable() ? 'button' : 'status'}
      aria-label={getStatusText()}
      tabIndex={isClickable() ? 0 : undefined}
      onKeyPress={isClickable() ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleConnect(); } : undefined}
    >
      <div className="status-icon">
        {isLoading || isConnecting ? (
          <RefreshIcon className="spinning" sx={{ fontSize: 18 }} aria-hidden="true" />
        ) : status.connected ? (
          <CloudDoneIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        ) : (
          <CloudOffIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        )}
      </div>
      <div className="status-indicator" aria-hidden="true"></div>
    </div>
  );
};

export default GoogleClassroomStatus;
